import { AdminElementsWrapper, SecondaryButton } from 'components';
import { StyledAdminCostAdjustment } from './styles';
import { useTranslation } from 'react-i18next';
import { useEffect, useId, useState, useRef, memo } from 'react';
interface IAdminCostAdjustmentProps {
  children?: React.ReactNode;
  heading: string;
  onSubmit: (value: number) => void;
  cost: number;
}
const AdminCostAdjustmentComponent = ({ heading, onSubmit, cost }: IAdminCostAdjustmentProps) => {
  const { t } = useTranslation();
  const formatNumber = (num: number) => {
    const fixed = num.toFixed(10);
    return fixed.replace(/\.?0+$/, '');
  };
  const [amount, setAmount] = useState(formatNumber(cost));
  const previousCostRef = useRef<number>(cost);
  const isUserEditingRef = useRef<boolean>(false);

  useEffect(() => {
    const formattedCost = formatNumber(cost);
    const formattedPreviousCost = formatNumber(previousCostRef.current);

    if (!isUserEditingRef.current && formattedCost !== formattedPreviousCost) {
      setAmount(formattedCost);
      previousCostRef.current = cost;
    } else if (formattedCost !== formattedPreviousCost) {
      previousCostRef.current = cost;
    }
  }, [cost]);

  const changeAmountHandler: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    isUserEditingRef.current = true;
    setAmount(event.target.value);
  };

  const submitHandler: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    isUserEditingRef.current = false;
    onSubmit(Number(amount));
  };

  const buttonIncrementHandler = () => {
    isUserEditingRef.current = true;
    setAmount((prev) => formatNumber(Number(prev) + 0.0001));
  };
  const buttonDecrementtHandler = () => {
    isUserEditingRef.current = true;
    setAmount((prev) => formatNumber(Number(prev) - 0.0001));
  };

  const inputId = useId();

  return (
    <StyledAdminCostAdjustment>
      <AdminElementsWrapper>
        <h3 className="heading">{heading}</h3>
        <form className="form" onSubmit={submitHandler}>
          <div className="input-group">
            <label htmlFor={inputId} hidden>
              {heading}
            </label>
            <input
              id={inputId}
              type="text"
              inputMode="decimal"
              className="input"
              value={amount}
              onChange={changeAmountHandler}
            />
            <div className="number-adjust">
              <button
                className="number-adjust-button incr"
                type="button"
                onClick={buttonIncrementHandler}
              >
                <svg
                  className="number-adjust-button-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="8"
                  viewBox="0 0 16 8"
                  fill="none"
                >
                  <path
                    d="M15 7L9.30158 2.11564C8.5526 1.47366 7.4474 1.47366 6.69842 2.11564L1 7"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                className="number-adjust-button decr"
                type="button"
                onClick={buttonDecrementtHandler}
              >
                <svg
                  className="number-adjust-button-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="8"
                  viewBox="0 0 16 8"
                  fill="none"
                >
                  <path
                    d="M15 1L9.30158 5.88436C8.5526 6.52634 7.4474 6.52634 6.69842 5.88436L1 0.999999"
                    strokeWidth="2"
                    strokeLinecap="round"
                    stroke="white"
                  />
                </svg>
              </button>
            </div>
          </div>
          <SecondaryButton type="submit" title={t('adminPage.saveButton')} outline />
        </form>
      </AdminElementsWrapper>
    </StyledAdminCostAdjustment>
  );
};

export const AdminCostAdjustment = memo(AdminCostAdjustmentComponent, (prevProps, nextProps) => {
  const formatNumber = (num: number) => {
    const fixed = num.toFixed(10);
    return fixed.replace(/\.?0+$/, '');
  };

  const costEqual = formatNumber(prevProps.cost) === formatNumber(nextProps.cost);
  const headingEqual = prevProps.heading === nextProps.heading;

  return costEqual && headingEqual;
});
